import { useState } from 'react'
import { discardRow, retryRow, type StuckRow } from '../services/sync/outbox'
import { kick, unblock } from '../services/sync/drainer'
import { syncBus } from '../services/sync/bus'
import { RotateCwIcon, TrashIcon, XIcon } from './icons'
import './StuckDialog.css'

/**
 * Gönderilemeyen kayıtlar (kullanıcı, 11 Eki 2026).
 *
 * Sunucunun reddettiği bir kayıt (çoğu zaman: garson "Götürdüm"e bastığında
 * kasa o masayı çoktan kapatmıştı) ve aynı adisyonun arkasındaki kayıtlar
 * kuyrukta kalıyor, rozet hep sarı görünüyordu. Burada ne olduğu yazıyor;
 * kişi tekrar deniyor ya da siliyor. Silmek onay istiyor: kayıt bu cihazdan
 * kaybolur.
 */

/** Kaydın ne olduğu, garsonun ve kasiyerin diliyle. */
function describe(row: StuckRow): string {
  if (row.table !== 'orders') return 'Kayıt'
  const p = row.ops.map((o) => o.payload)
  if (row.ops.some((o) => o.type === 'insert')) {
    return p.some((x) => x.status === 'completed') ? 'Satış' : 'Masa açılışı'
  }
  if (p.some((x) => x.status === 'completed')) return 'Ödeme'
  if (p.some((x) => x.status === 'void')) return 'İptal'
  if (p.some((x) => 'lines' in x)) {
    return p.every((x) => !('lines' in x) || x.silent === true) ? 'Götürdüm / düzeltme' : 'Masaya ürün ekleme'
  }
  if (p.some((x) => 'table_name' in x)) return 'Masa taşıma'
  return 'Adisyon değişikliği'
}

const time = (ms: number) =>
  new Date(ms).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export function StuckDialog({ rows, onClose }: { rows: StuckRow[]; onClose: () => void }) {
  /** Silmek için onay bekleyen satır. */
  const [confirming, setConfirming] = useState<string | null>(null)

  const retry = async (r: StuckRow) => {
    await retryRow(r.table, r.rowId)
    unblock(r.table, r.rowId)
    syncBus.emit('outbox-changed')
    kick()
  }

  const discard = async (r: StuckRow) => {
    await discardRow(r.table, r.rowId)
    unblock(r.table, r.rowId)
    setConfirming(null)
    syncBus.emit('outbox-changed')
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-label="Gönderilemeyen kayıtlar">
      <div className="dlg__card stuck">
        <div className="stuck__head">
          <h2>Gönderilemeyen kayıtlar</h2>
          <button type="button" className="stuck__x" onClick={onClose} aria-label="Kapat">
            <XIcon />
          </button>
        </div>

        {rows.length === 0 ? (
          <p className="stuck__empty">Takılan kayıt kalmadı. Hepsi kaydedildi ya da silindi.</p>
        ) : (
          <>
            <p className="stuck__lead">
              Bu kayıtlar sunucuya gitmedi. Çoğu zaman adisyon başka bir cihazdan kapatılmış ya da
              iptal edilmiştir; o durumda silmek güvenlidir.
            </p>
            <ul className="stuck__list">
              {rows.map((r) => {
                const key = `${r.table}:${r.rowId}`
                return (
                  <li key={key}>
                    <div className="stuck__info">
                      <b>
                        {r.label || 'Adisyon'}, {describe(r)}
                      </b>
                      <span className="stuck__reason">{r.reason}</span>
                      <small>{time(r.since)}</small>
                    </div>
                    {confirming === key ? (
                      <div className="stuck__actions">
                        <span className="stuck__ask">Bu değişiklik silinsin mi?</span>
                        <button type="button" className="btn btn--ghost" onClick={() => setConfirming(null)}>
                          Vazgeç
                        </button>
                        <button type="button" className="btn stuck__del" onClick={() => void discard(r)}>
                          <TrashIcon />
                          Sil
                        </button>
                      </div>
                    ) : (
                      <div className="stuck__actions">
                        <button type="button" className="btn btn--outline" onClick={() => void retry(r)}>
                          <RotateCwIcon />
                          Tekrar dene
                        </button>
                        <button type="button" className="btn btn--ghost stuck__del-ask" onClick={() => setConfirming(key)}>
                          <TrashIcon />
                          Sil
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
